import React from 'react';
import {
  Dialog, DialogContent, DialogActions, Button, TextField, Box, Typography, Alert,
  FormControlLabel, Checkbox, CircularProgress, Chip, Tooltip,
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SendIcon from '@mui/icons-material/Send';
import { userService } from '../Scripts/UserService';

const BROWN = '#4d3e34';
const YELLOW = '#ffde59';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// A user can only be edited until they sign up: no uid yet.
export const isNotOnboarded = (user) => !user?.uid || user.uid === 'N/A';

// What to show for a person's onboarding email: the delivery result when known, or "not sent" for a bulk-imported
// person whose email was never recorded as sent (e.g. Resend refused it). Older users have neither and show nothing.
export const emailStatusOf = (user) => user?.email_status
  || (isNotOnboarded(user) && user?.import_id && !user?.onboarding_sent_at ? 'not_sent' : null);

// Delivery state of the onboarding email, shown next to the email address.
export function EmailStatusChip({ status }) {
  const map = {
    bounced: { label: 'Email bounced', bg: '#ffeaea', color: '#b82b2b', border: '#f5c6c6', tip: 'The onboarding email could not be delivered. Correct the address and resend.' },
    complained: { label: 'Marked as spam', bg: '#fff4e0', color: '#b35c00', border: '#ffd699', tip: 'The recipient marked the onboarding email as spam.' },
    not_sent: { label: 'Email not sent', bg: '#fff4e0', color: '#b35c00', border: '#ffd699', tip: 'The onboarding email was not sent. Open Edit contact and use Resend onboarding.' },
    delivered: { label: 'Email delivered', bg: '#eafff0', color: '#1a7a3a', border: '#b6e8c4', tip: 'The onboarding email reached the inbox.' },
  };
  const m = map[ status ];
  if (!m) return null;
  return (
    <Tooltip title={m.tip} placement="top">
      <Chip size="small" label={m.label} sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, bgcolor: m.bg, color: m.color, border: `1px solid ${m.border}` }} />
    </Tooltip>
  );
}

/**
 * Edit the email / id of a user who has not signed up yet, and resend their onboarding email.
 * The server re-checks "not onboarded" on every call — this dialog is just the front door.
 *
 * Props: open, user { employee_id, name, email, email_status, designation }, onClose,
 *        onUpdated(patch)  patch = { employee_id, email, email_status }, showToast(message, severity)
 */
export default function UserContactDialog({ open, user, onClose, onUpdated, showToast }) {
  const [ email, setEmail ] = React.useState('');
  const [ userId, setUserId ] = React.useState('');
  const [ sendOnboarding, setSendOnboarding ] = React.useState(true);
  const [ busy, setBusy ] = React.useState(null); // 'save' | 'resend' | null
  const [ error, setError ] = React.useState(null); // { message, suggestion }

  // Re-seed the form every time the dialog opens for a user.
  React.useEffect(() => {
    if (open && user) {
      setEmail(user.email && user.email !== 'N/A' ? user.email : '');
      setUserId(user.employee_id || '');
      setSendOnboarding(true);
      setError(null);
      setBusy(null);
    }
  }, [ open, user ]);

  if (!user) return null;

  const cleanEmail = email.trim().toLowerCase();
  const cleanId = userId.trim();
  const emailChanged = cleanEmail !== (user.email || '').toLowerCase();
  const idChanged = cleanId !== user.employee_id;
  const dirty = emailChanged || idChanged;
  const bounced = user.email_status === 'bounced';

  const localProblem = () => {
    if (!cleanId) return 'User ID cannot be empty.';
    if (/\s/.test(cleanId)) return 'User ID cannot contain spaces.';
    if (/[.$#[\]/]/.test(cleanId)) return 'User ID cannot contain . $ # [ ] /';
    if (!EMAIL_RE.test(cleanEmail)) return 'Enter a valid email address.';
    return null;
  };

  const save = async () => {
    const problem = localProblem();
    if (problem) { setError({ message: problem }); return; }
    setBusy('save');
    setError(null);
    try {
      const res = await userService.updateUserContact({
        designation: user.designation,
        userId: user.employee_id,
        ...(emailChanged ? { newEmail: cleanEmail } : {}),
        ...(idChanged ? { newUserId: cleanId } : {}),
        sendOnboarding: emailChanged && !idChanged ? sendOnboarding : false,
      });
      const sentNote = res.onboardingEmail === 'sent' || res.onboardingEmail === 'sent_automatically'
        ? ' Onboarding email sent.'
        : res.onboardingEmail === 'failed' ? ' The onboarding email could not be sent — use Resend.' : '';
      showToast?.(`Details updated.${sentNote}`, res.onboardingEmail === 'failed' ? 'warning' : 'success');
      onUpdated?.({ employee_id: res.userId, email: res.email, email_status: null });
      onClose();
    } catch (e) {
      setError({ message: e.message, suggestion: e.suggestion });
    } finally {
      setBusy(null);
    }
  };

  const resend = async () => {
    setBusy('resend');
    setError(null);
    try {
      const res = await userService.resendOnboarding(user.designation, user.employee_id);
      showToast?.(`Onboarding email sent to ${res.email}.`, 'success');
      onClose();
    } catch (e) {
      setError({ message: e.message });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs"
      PaperProps={{ sx: { borderRadius: 3, backgroundColor: '#fffef7', overflow: 'hidden' } }}>
      <Box sx={{ px: 3, py: 2, background: 'linear-gradient(135deg,#ffde59,#ffeb9a)', borderBottom: `3px solid ${BROWN}` }}>
        <Typography sx={{ color: BROWN, fontWeight: 800, fontSize: 18 }}>Edit contact details</Typography>
        <Typography variant="caption" sx={{ color: BROWN, opacity: 0.75 }}>
          {user.name && user.name !== 'Awaiting Onboarding' ? `${user.name} • ` : ''}{user.designation === 'Teachers' ? 'Faculty' : 'Student'} • not signed up yet
        </Typography>
      </Box>

      <DialogContent sx={{ pt: 3 }}>
        <Typography variant="body2" sx={{ color: '#6b5a4b', mb: 2.5 }}>
          Fix a wrong ID or email. This is only possible until the person signs up.
        </Typography>

        <TextField fullWidth size="small" label="User ID" value={userId} onChange={(e) => setUserId(e.target.value)}
          disabled={!!busy} sx={{ mb: 2 }} />

        <TextField fullWidth size="small" label="Email" value={email} onChange={(e) => setEmail(e.target.value)}
          disabled={!!busy} type="email"
          InputProps={{ startAdornment: <MailOutlineIcon sx={{ color: '#a1a1aa', mr: 1, fontSize: 20 }} /> }} />

        {user.email_status && (
          <Box sx={{ mt: 1 }}><EmailStatusChip status={user.email_status} /></Box>
        )}

        {idChanged && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Changing the ID sends a fresh onboarding email with the new ID automatically.
          </Alert>
        )}
        {emailChanged && !idChanged && (
          <FormControlLabel sx={{ mt: 1.5 }}
            control={<Checkbox size="small" checked={sendOnboarding} onChange={(e) => setSendOnboarding(e.target.checked)} sx={{ color: BROWN, '&.Mui-checked': { color: BROWN } }} />}
            label={<Typography variant="body2">Send the onboarding email to the new address</Typography>} />
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}
            action={error.suggestion ? (
              <Button color="inherit" size="small" onClick={() => { setEmail(error.suggestion); setError(null); }}>Use it</Button>
            ) : null}>
            {error.message}
            {error.suggestion && <><br />Did you mean <strong>{error.suggestion}</strong>?</>}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Tooltip placement="top" title={bounced ? 'The last email to this address bounced. Correct the email first.' : dirty ? 'Save your changes first.' : ''}>
          <span>
            <Button onClick={resend} disabled={!!busy || dirty || bounced || !user.email || user.email === 'N/A'}
              startIcon={busy === 'resend' ? <CircularProgress size={14} /> : <SendIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none', fontWeight: 700, color: BROWN, border: `1px solid ${BROWN}`, borderRadius: 2, px: 2 }}>
              Resend onboarding
            </Button>
          </span>
        </Tooltip>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} disabled={!!busy} sx={{ textTransform: 'none', color: BROWN, fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={!!busy || !dirty}
            startIcon={busy === 'save' ? <CircularProgress size={14} sx={{ color: YELLOW }} /> : null}
            sx={{ textTransform: 'none', fontWeight: 800, bgcolor: BROWN, color: YELLOW, borderRadius: 2, px: 3, '&:hover': { bgcolor: '#3a2f27' }, '&.Mui-disabled': { opacity: 0.45, color: YELLOW, bgcolor: BROWN } }}>
            Save changes
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
